import { TextBlock } from '../../utils/get-map-change-description';
import { computeTextWidth } from '../../utils/compute-text-width';

type LimitedEllipsisTextProps = {
  maxWidth: number;
  textBlocks: TextBlock[];
};

const FONT = 'italic 16px ' + getComputedStyle(document.documentElement).getPropertyValue('--font-family');
const TEXT_PADDING_RIGHT = 16;

export const LimitedEllipsisText = ({ maxWidth, textBlocks }: LimitedEllipsisTextProps) => {
  // calculate width of all static texts that should not be shortened
  const staticTextsWidthSum = textBlocks
    .filter(({ type }) => type == 'static')
    .map((x) => computeTextWidth(x.text, FONT))
    .reduce((a, b) => a + b, 0);

  // the remaining width can be shared between the dynamic texts
  let remainingDynamicTextsWidth = maxWidth - staticTextsWidthSum - TEXT_PADDING_RIGHT;
  let remainingDynamicTextsCount = textBlocks.filter(({ type }) => type == 'dynamic').length;

  // calculate widths for texts
  const textsWithWidths: (TextBlock & { width?: number; originalWidth?: number })[] = textBlocks.map((x) => {
    if (x.type == 'dynamic') {
      let originalWidth = computeTextWidth(x.text, FONT);

      // width is the smaller value between the original node's width
      // and the maximal available width of this dynamic text
      // (= an nth part of the remaining available width when it needs to be shared between n more dynamic texts)
      let width = Math.min(originalWidth, remainingDynamicTextsWidth / remainingDynamicTextsCount);

      if (originalWidth != width) {
        // handle ellipsis width
        width = width - 6;
      }

      // remove the used width from the remaining width
      remainingDynamicTextsWidth = remainingDynamicTextsWidth - width;

      // one less dynamic text to share the remaining width with
      remainingDynamicTextsCount = remainingDynamicTextsCount - 1;

      return { ...x, width, originalWidth };
    }
    return x;
  });

  return (
    <span>
      {textsWithWidths.map(({ text, type, textDecoration, fontStyle, width, originalWidth }, i) => {
        const spaceSuffix = textsWithWidths[i + 1] ? ' ' : '';
        return (
          <>
            {type == 'dynamic' ? (
              <span
                style={{
                  width,
                  textDecoration,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                  display: 'inline-block',
                  fontStyle,
                }}
              >
                {text}
              </span>
            ) : (
              <span style={{ textDecoration, fontStyle }}>{text}</span>
            )}
            {/* if ellipsis are shown hide the space suffix */}
            {originalWidth != width ? '' : spaceSuffix}
          </>
        );
      })}
    </span>
  );
};
